import { memo, useCallback, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChannelContext, ConfigContext, UserContext } from '../../../../../context';
import {
  Event,
  EventStatus,
  NotificationsTabType,
  useEventChangeLogByIdQuery,
  useEventParticipantsGenerateReportMutation,
  useStoriesByChannelIdQuery,
} from '../../../../../services';
import { eventCancel, eventDelete, updateCardEvent } from '../../../../../slices';
import { COPY_EVENT_COOKIE_KEY, NOTIFICATIONS_ADMIN_COOKIE_KEY } from '../../../../constants';
import {
  AbuseReportType,
  DSAReportType,
  useAbuseReportPopup,
  useDSAReportPopup,
} from '../../../../hooks';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  CookieActionType,
  CookieExpirationType,
  cookieOptions,
  getChannelUrl,
  getCssVar,
  getOrganisationDomain,
  isAdminLayout,
  isEventPast,
} from '../../../../utils';
import { ChangeLogModal } from '../../../ChangeLogModal';
import { ConfirmationModal } from '../../../ConfirmationModal';
import { CreateCardModal } from '../../../CreateCard';
import { Deeplink, DeeplinkType } from '../../../Deeplink';
import { DropdownMenu } from '../../../DropdownMenu';
import { IconLabel } from '../../../IconLabel';
import { Popup } from '../../../Popup';
import { EditChannelsModal } from '../../EditChannelsModal';

import dropdownMenuClasses from '../../../DropdownMenu/DropdownMenu.module.scss';

interface EventActionsMenuProps {
  event: Event;
  onEdit: () => void;
  onDeleteSuccess?: () => void;
  className?: string;
}

export const EventActionsMenu = memo(
  ({ event, onEdit, onDeleteSuccess, className }: EventActionsMenuProps) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'eventActions' });

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const [eventParticipantsGenerateReport] = useEventParticipantsGenerateReportMutation();

    const [, setCookie] = useCookies([COPY_EVENT_COOKIE_KEY, NOTIFICATIONS_ADMIN_COOKIE_KEY]);

    const { id: channelId } = useContext(ChannelContext).channel;

    const { data: postInStories = [] } = useStoriesByChannelIdQuery({
      channelId,
      filter: { onlyPostIn: true },
    });

    const {
      addContent: { excludeAdminEventPost, excludeReaderEventPost },
      abuseReport: { enabled: abuseReportEnabled },
      dsaReport: { enabled: dsaReportEnabled },
    } = useContext(ConfigContext).config.elements;

    const { isAnonymous } = useContext(UserContext).userProfile;

    const { id: eventId, name: eventName, permissions, startAt, endAt, status } = event;

    const { allowToDelete, allowToEdit, allowToEditChannels, allowToSeeChangeLog } = permissions;

    const { abuseReportModal, abuseReportHandler } = useAbuseReportPopup({
      type: AbuseReportType.Event,
      eventId,
      eventName,
    });

    const { DSAReportModal, DSAReportHandler } = useDSAReportPopup({
      type: DSAReportType.Event,
      eventId,
      eventName,
    });

    const isPast = isEventPast(startAt, endAt);

    const isCancelled = status === EventStatus.CANCELLED;

    const hideEventPost = isAdminLayout() ? excludeAdminEventPost : excludeReaderEventPost;

    const hasMenuItems = isAdminLayout() || allowToDelete || !isCancelled || !hideEventPost;

    const [isMenuOpened, setIsMenuOpened] = useState(false);

    const [isCreateEventCardModalOpen, setIsCreateEventCardModalOpen] = useState(false);

    const [isDeleteConfirmOpened, setIsDeleteConfirmOpened] = useState(false);

    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const [isCancelConfirmOpened, setIsCancelConfirmOpened] = useState(false);

    const [isCancelLoading, setIsCancelLoading] = useState(false);

    const [isEditChannelsOpened, setIsEditChannelsOpened] = useState(false);

    const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);

    const { data: changeLogItems = [], isFetching: isChangeLogLoading } =
      useEventChangeLogByIdQuery({ channelId, eventId }, { skip: !isChangeLogOpen });

    const onCreateEventCard = useCallback(() => {
      setIsMenuOpened(false);
      setIsCreateEventCardModalOpen(true);
    }, []);

    const onCopy = useCallback(() => {
      setIsMenuOpened(false);

      setCookie(
        COPY_EVENT_COOKIE_KEY,
        {
          organisation: getOrganisationDomain(),
          sourceChannelId: channelId,
          sourceEventId: eventId,
        },
        cookieOptions({ action: CookieActionType.SET, expiration: CookieExpirationType.DAY })
      );

      toast(ctxT('copied'));
    }, [channelId, eventId, setCookie, ctxT]);

    const onPush = useCallback(() => {
      setIsMenuOpened(false);

      setCookie(
        NOTIFICATIONS_ADMIN_COOKIE_KEY,
        { selectedTabIndex: NotificationsTabType.PUSH },
        cookieOptions()
      );
      navigate(`/admin/notifications?eventId=${eventId}`);
    }, [eventId, navigate, setCookie]);

    const onExport = useCallback(async () => {
      setIsMenuOpened(false);

      try {
        await eventParticipantsGenerateReport({ channelId, eventId }).unwrap();
        toast(t('common.report-sent'));
      } catch {
        toast.error(t('common.error-message'));
      }
    }, [channelId, eventId, eventParticipantsGenerateReport, t]);

    const onDownload = useCallback(() => {
      setIsMenuOpened(false);
      window.open(`${getChannelUrl()}/events/${eventId}/calendar.ics`, '_blank');
    }, [eventId]);

    const onEditClick = useCallback(() => {
      setIsMenuOpened(false);
      onEdit();
    }, [onEdit]);

    const onEditChannels = useCallback(() => {
      setIsMenuOpened(false);
      setIsEditChannelsOpened(true);
    }, []);

    const onChangeLog = useCallback(() => {
      setIsMenuOpened(false);
      setIsChangeLogOpen(true);
    }, []);

    const onAbuseReport = useCallback(() => {
      setIsMenuOpened(false);
      abuseReportHandler();
    }, [abuseReportHandler]);

    const onDSAReport = useCallback(() => {
      setIsMenuOpened(false);
      DSAReportHandler();
    }, [DSAReportHandler]);

    const onCancel = useCallback(() => {
      setIsMenuOpened(false);
      setIsCancelConfirmOpened(true);
    }, []);

    const onCancelAccept = useCallback(async () => {
      setIsCancelLoading(true);

      try {
        await dispatch(
          eventCancel({ channelId, eventId, fields: { status: EventStatus.CANCELLED } })
        );
        await dispatch(updateCardEvent({ channelId, eventId }));
        setIsCancelConfirmOpened(false);
      } catch {
        toast.error(t('common.error-message'));
      } finally {
        setIsCancelLoading(false);
      }
    }, [dispatch, channelId, eventId, t]);

    const onDelete = useCallback(() => {
      setIsMenuOpened(false);
      setIsDeleteConfirmOpened(true);
    }, []);

    const onAcceptDelete = useCallback(async () => {
      setIsDeleteLoading(true);

      try {
        await dispatch(eventDelete({ channelId, eventId }));
        setIsDeleteConfirmOpened(false);
        onDeleteSuccess?.();
      } catch {
        toast.error(t('common.error-message'));
      } finally {
        setIsDeleteLoading(false);
      }
    }, [dispatch, channelId, eventId, onDeleteSuccess, t]);

    const menuItems = (
      <>
        {!hideEventPost && (
          <IconLabel
            iconId={'article'}
            iconSize={18}
            label={ctxT('create-event-card')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onCreateEventCard}
            singleColor
          />
        )}
        {abuseReportEnabled && (
          <IconLabel
            iconId={'report'}
            iconSize={18}
            label={t('abuseReport.label')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onAbuseReport}
            singleColor
          />
        )}
        {dsaReportEnabled && (
          <IconLabel
            iconId={'problem'}
            iconSize={18}
            label={t('DSAReport.label')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onDSAReport}
            singleColor
          />
        )}
        {isAdminLayout() && (
          <>
            {!isCancelled && (
              <IconLabel
                iconId={'copy'}
                iconSize={18}
                label={ctxT('copy')}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={onCopy}
                singleColor
              />
            )}
            <IconLabel
              iconId={'bell'}
              iconSize={18}
              label={ctxT('push')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={onPush}
              singleColor
            />
            <Deeplink
              type={DeeplinkType.EVENT_DRAFT}
              eventId={eventId}
              label={ctxT('preview-link')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={() => setIsMenuOpened(false)}
            />
            <Deeplink
              type={DeeplinkType.EVENT}
              eventId={eventId}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={() => setIsMenuOpened(false)}
            />
            {!isCancelled && (
              <IconLabel
                iconId={'users'}
                iconSize={18}
                label={ctxT('export-participants')}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={onExport}
                singleColor
              />
            )}
          </>
        )}
        {!isCancelled && (
          <IconLabel
            iconId={'donwload'}
            iconSize={18}
            label={t('common.download')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onDownload}
            singleColor
          />
        )}
        {allowToSeeChangeLog && (
          <IconLabel
            iconId={'clock'}
            iconSize={18}
            label={t('changeLog.label')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onChangeLog}
            singleColor
          />
        )}
        {allowToEdit && !isPast && !isCancelled && (
          <IconLabel
            iconId={'edit'}
            iconSize={18}
            label={t('common.edit')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onEditClick}
            singleColor
          />
        )}
        {allowToEditChannels && !isCancelled && (
          <IconLabel
            iconId={'channels'}
            iconSize={18}
            label={ctxT('edit-channels')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onEditChannels}
            singleColor
          />
        )}
        {allowToEdit && !isPast && !isCancelled && (
          <IconLabel
            iconId={'close'}
            iconSize={18}
            label={t('common.cancel')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onCancel}
            singleColor
          />
        )}
        {allowToDelete && (
          <IconLabel
            iconId={'delete'}
            iconSize={18}
            label={t('common.delete')}
            className={dropdownMenuClasses['dropdown-menu__item--small']}
            onClick={onDelete}
            color={getCssVar('--color-danger')}
            singleColor
          />
        )}
      </>
    );

    if (isAnonymous || !hasMenuItems) {
      return null;
    }

    return (
      <>
        <Popup
          isOpen={isMenuOpened}
          setIsOpen={setIsMenuOpened}
          iconId={'dots-menu'}
          ariaLabel={t('common.more-options')}
          color={getCssVar('--action-pane-icon-color')}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          bodyTop={'1.5rem'}
          bodyRight={'0.125rem'}
          body={<DropdownMenu width={'12rem'} maxHeight={'15rem'} content={menuItems} />}
          parentClassName={className}
        />

        {isCreateEventCardModalOpen && (
          <CreateCardModal
            isOpen={isCreateEventCardModalOpen}
            onClose={() => setIsCreateEventCardModalOpen(false)}
            postInStories={postInStories}
            eventPostFilledFields={{ event, headline: '' }}
          />
        )}

        {isEditChannelsOpened && (
          <EditChannelsModal
            isOpened={isEditChannelsOpened}
            onClose={() => setIsEditChannelsOpened(false)}
            event={event}
          />
        )}

        <ConfirmationModal
          isOpen={isCancelConfirmOpened}
          onClose={() => setIsCancelConfirmOpened(false)}
          onAccept={onCancelAccept}
          title={ctxT('cancel-confirmation-title')}
          subTitle={ctxT('cancel-confirmation-sub-title')}
          acceptLabel={t('common.cancel')}
          loading={isCancelLoading}
          keepOpened={isCancelLoading}
          danger
        />

        <ConfirmationModal
          isOpen={isDeleteConfirmOpened}
          onClose={() => setIsDeleteConfirmOpened(false)}
          onAccept={onAcceptDelete}
          title={ctxT('delete-confirmation-title')}
          subTitle={ctxT('delete-confirmation-sub-title')}
          acceptLabel={t('common.delete')}
          loading={isDeleteLoading}
          keepOpened={isDeleteLoading}
          danger
        />

        <ChangeLogModal
          isLoading={isChangeLogLoading}
          isOpen={isChangeLogOpen}
          items={changeLogItems}
          onClose={() => setIsChangeLogOpen(false)}
        />

        {abuseReportModal}

        {DSAReportModal}
      </>
    );
  }
);
